import { isDirty } from 'redux-form';
import { createSelector } from 'reselect';

import { RootState } from '../../reducers';
import { getCurrencyCodeList } from '../code/code.selector';
import { CodeElement } from '../code/code.types';
import { SettingForms, ThirdParty } from './settings.types';

export const getSettings = (state: RootState) => state.entities.settings;

export const getCurrency = (state: RootState): string => state.entities.settings.otherSettings.currency;
export const getTimeZone = (state: RootState): string => state.entities.settings.otherSettings.timeZone;

export const getForms = (state: RootState) => state.form;

export const getRationEntryMethod = (state: RootState): number =>
  state.entities.settings.otherSettings.rationEntryMethod;

export const getUseZoneControl = (state: RootState): boolean => state.entities.settings.otherSettings.useZoneControl;

export const getCurrencyString = createSelector(
  [getCurrencyCodeList, getCurrency],
  (codes: CodeElement[], currency: string) => {
    const currencyCode = codes.find((item) => item.description2 === currency);
    return currencyCode && currencyCode.description3;
  },
);

export const isDollar = createSelector([getCurrency], (currency) => ['USD', 'AUD'].includes(currency));

export const isListMode = (state: RootState): boolean => state.entities.settings.loadOptimization.feedingSendMode === 2;

export const isPremixAmountsByPercentage = (state: RootState): boolean =>
  state.entities.settings.otherSettings.premixAmountsByPercentage;

export const settingsChanged = createSelector([getForms, (state: RootState) => state], (forms, state) => {
  const formNames = Object.keys(forms);
  const filteredForms = formNames.filter(
    (form) => SettingForms.filter((elem) => form.search(elem) !== -1).length !== 0,
  );
  return filteredForms.some((form) => isDirty(form)(state));
});

export const getDefaultDensity = (state: RootState) => state.entities.settings.defaultDensity;

export const getRefusalSubtractModeDefault = (state: RootState) =>
  state.entities.settings.otherSettings.refusalSubtractModeDefault;

export const getDLCommunicationStarted = (state: RootState) => state.entities.settings.dlCommunicationStarted;

export const getUseWeightMotionTolerance = (state: RootState) =>
  state.entities.settings.otherSettings.useWeightMotionTolerance;

export const getUseLockedFeedingsSetting = (state: RootState) =>
  state.entities.settings.otherSettings.useLockedFeedings;

export const getInventorySettings = (state: RootState) => state.entities.settings.inventorySettings;
export const getOtherSettings = (state: RootState) => state.entities.settings.otherSettings;
export const getWeighBridgeSystemSettings = (state: RootState) => state.entities.settings.weighbridgeSettings;

export const getWeighBridgeSystemsList = createSelector(
  getWeighBridgeSystemSettings,
  (settings) => settings.weighBridgeSystemsList,
);

export const getThirdParties = (state: RootState) => state.entities.settings.thirdParties;

export const getActiveThirdParty = createSelector(getThirdParties, (thirdParties: ThirdParty[]) =>
  thirdParties.filter((party) => party.active),
);

export const getBunkScoreGrades = (state: RootState) => state.entities.settings.bunkReaderScoreSettings.bunkScoreGrades;
export const getBunkReaderScoreSettings = (state: RootState) => state.entities.settings.bunkReaderScoreSettings;

export const getScaleWeightUnit = (state: RootState) => state.entities.settings.otherSettings.scaleWeightUnit;

export const getExternalfeedingSystemmSettings = (state: RootState) =>
  state.entities.settings.externalFeedingSystemsSettings;
export const getFSAdvancedSettings = (state: RootState) =>
  state.entities.settings.externalFeedingSystemsSettings.fsAdvancedSettings;
